import type React from 'react';

import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { type ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Germany from '@/assets/icons/flags/de.svg';
import France from '@/assets/icons/flags/fr.svg';
import UnitedKingdom from '@/assets/icons/flags/gb.svg';
import { TopBarLanguageChangeLanguageIconStyles } from '@/layouts/private/topBar/components/languageChange/style/TopBarLanguageChange.style.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type OptionType = {
  label: ReactNode;
  value: string;
};

export const TopBarLanguageChange: React.FC = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslationWithPrefix('layouts.topBar.language');
  const { t: tTooltip } = useTranslationWithPrefix('layouts.topBar.tooltip');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangeLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    void handleLanguageChange(i18n.language)();
  }, []);

  const handleLanguageChange = (language: string) => async () => {
    await i18n.changeLanguage(language);
    document.documentElement.lang = language;
    handleChangeLanguageMenuClose();
  };

  const languageOptions: OptionType[] = [
    {
      label: (
        <>
          <Box
            alt={t('english')}
            component="img"
            src={UnitedKingdom}
            sx={{ mr: 1, width: '32px' }}
          />
          <Typography>{t('english')}</Typography>
        </>
      ),
      value: 'en',
    },
    {
      label: (
        <>
          <Box alt={t('german')} component="img" src={Germany} sx={{ mr: 1, width: '32px' }} />
          <Typography>{t('german')}</Typography>
        </>
      ),
      value: 'de',
    },
    {
      label: (
        <>
          <Box alt={t('french')} component="img" src={France} sx={{ mr: 1, width: '32px' }} />
          <Typography>{t('french')}</Typography>
        </>
      ),
      value: 'fr',
    },
  ];

  const returnSelectedLanguagesFlag = () => {
    switch (i18n.language) {
      case 'de':
        return Germany;
      case 'en':
        return UnitedKingdom;
      case 'fr':
        return France;
      default:
        return UnitedKingdom;
    }
  };

  return (
    <Box>
      <Tooltip title={open ? '' : tTooltip('language')}>
        <IconButton onClick={handleClick} size="large">
          <Box sx={TopBarLanguageChangeLanguageIconStyles}>
            <Box
              alt={t('selectedLanguage')}
              component="img"
              src={returnSelectedLanguagesFlag()}
              sx={{ width: '22px' }}
            />
          </Box>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} onClose={handleChangeLanguageMenuClose} open={open}>
        {languageOptions.map(option => (
          <MenuItem key={option.value} onClick={handleLanguageChange(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
